import React from 'react';
import { LucideIcon } from 'lucide-react';
import Tooltip, { type TooltipPosition } from './Tooltip';
import { useActionLoading } from '../../hooks/useActionLoading';

interface ActionIconButtonProps {
  icon: LucideIcon;
  onClick: (e?: React.MouseEvent) => void | Promise<void>;
  variant?: 'edit' | 'delete' | 'view' | 'contact' | 'project' | 'ghost';
  size?: 'sm' | 'md';
  className?: string;
  tooltip?: string;
  /** Defaults to Tooltip default (`bottom`). Override only when layout requires it (e.g. sidebar → `right`). */
  tooltipPosition?: TooltipPosition;
  disabled?: boolean;
}

const ActionIconButton: React.FC<ActionIconButtonProps> = ({
  icon: Icon,
  onClick,
  variant = 'edit',
  size = 'sm',
  className = '',
  tooltip,
  tooltipPosition,
  disabled = false,
}) => {
  const { isLoading, executeWithLoading, LoadingIndicator } = useActionLoading();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const getVariantStyles = () => {
    const baseStyles = 'p-2 rounded-lg transition-all duration-200 flex-shrink-0 hover:scale-105 active:scale-95';
    switch (variant) {
      case 'edit':
        return `${baseStyles} text-primary-600 hover:text-primary-700 hover:bg-primary-50 active:bg-primary-100`;
      case 'delete':
        return `${baseStyles} text-red-500 hover:text-red-600 hover:bg-red-50 active:bg-red-100`;
      case 'view':
        return `${baseStyles} text-primary-600 hover:text-primary-700 hover:bg-primary-50 active:bg-primary-100`;
      case 'contact':
        return `${baseStyles} text-primary-600 hover:text-primary-700 hover:bg-primary-50 active:bg-primary-100`;
      case 'project':
        return `${baseStyles} text-primary-600 hover:text-primary-700 hover:bg-primary-50 active:bg-primary-100`;
      case 'ghost':
        // No hover fill — for use on white / already light surfaces
        return `${baseStyles} text-primary-600 hover:text-primary-700`;
      default:
        return `${baseStyles} text-primary-600 hover:text-primary-700 hover:bg-primary-50 active:bg-primary-100`;
    }
  };

  const getIconSize = () => {
    return size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  };

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const result = onClick(e) as void | Promise<void>;
    // Avoid loading flash / opacity jump on sync actions (e.g. reorder).
    if (result && typeof (result as Promise<void>).then === 'function') {
      await executeWithLoading(() => result, e);
    }
  };

  const button = (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={disabled ? undefined : handleClick}
        disabled={disabled || isLoading}
        className={`${getVariantStyles()} ${disabled ? 'opacity-40 cursor-not-allowed hover:scale-100 active:scale-100' : ''} ${isLoading ? 'opacity-60' : ''} ${className}`}
      >
        <Icon className={getIconSize()} />
      </button>
      <LoadingIndicator />
    </>
  );

  if (tooltip) {
    return (
      <Tooltip content={tooltip} position={tooltipPosition}>
        {button}
      </Tooltip>
    );
  }

  return button;
};

export default ActionIconButton;
